import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { tasksAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Calendar as CalendarIcon, 
  ChevronLeft, 
  ChevronRight,
  Loader2,
  X,
  Move,
  MousePointer
} from 'lucide-react';
import Layout from '../components/Layout';
import { TranslatableSpan } from '../components/TranslatableText';

// Priority colors
const PRIORITY_COLORS = {
  critical: { bg: '#ef4444', border: '#dc2626', text: '#fff' },
  high: { bg: '#f59e0b', border: '#d97706', text: '#000' },
  medium: { bg: '#3b82f6', border: '#2563eb', text: '#fff' },
  low: { bg: '#6b7280', border: '#4b5563', text: '#fff' }
};

const CalendarPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const calendarRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  
  // Custom popover state
  const [popoverData, setPopoverData] = useState(null);
  
  // Move mode state - when user wants to move an event from popover
  const [moveMode, setMoveMode] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const tasksRes = await tasksAPI.getAll();
      setTasks(tasksRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  };

  // Convert tasks to FullCalendar events
  const events = tasks
    .filter(task => task.deadline)
    .map(task => {
      const priority = task.priority || 'medium';
      const colors = PRIORITY_COLORS[priority];
      
      return {
        id: task.id,
        title: task.title,
        start: task.deadline,
        allDay: true,
        backgroundColor: colors.bg,
        borderColor: colors.border,
        textColor: colors.text,
        extendedProps: {
          task: task
        }
      };
    });

  // Custom event content renderer with translation
  const renderEventContent = (eventInfo) => {
    return (
      <div className="fc-event-title-container">
        <div className="fc-event-title fc-sticky">
          <TranslatableSpan text={eventInfo.event.title} />
        </div>
      </div>
    );
  };

  // Handle event click - navigate to task detail
  const handleEventClick = (info) => {
    if (moveMode) return; // Don't navigate if in move mode
    navigate(`/tasks/${info.event.id}`);
  };

  // Handle event drop (drag and drop from calendar)
  const handleEventDrop = async (info) => {
    const taskId = info.event.id;
    const newDate = info.event.start.toISOString();
    const task = tasks.find(t => t.id === taskId);
    
    try {
      await tasksAPI.update(taskId, { deadline: newDate });
      toast.success(`"${task?.title}" rescheduled to ${info.event.start.toLocaleDateString()}`);
      
      setTasks(prev => prev.map(t => 
        t.id === taskId ? { ...t, deadline: newDate } : t
      ));
      
      setPopoverData(null);
    } catch (err) {
      toast.error('Failed to reschedule task');
      info.revert();
    }
  };

  // Handle "more" link click - show custom popover
  const handleMoreLinkClick = (info) => {
    const events = info.allSegs.map(seg => ({
      id: seg.event.id,
      title: seg.event.title,
      backgroundColor: seg.event.backgroundColor,
      textColor: seg.event.textColor,
      task: seg.event.extendedProps.task
    }));
    
    setPopoverData({
      date: info.date,
      events: events,
      position: {
        x: info.jsEvent.pageX,
        y: info.jsEvent.pageY
      }
    });
    
    return 'none';
  };

  // Handle date click - if in move mode, move the event here
  const handleDateClick = async (info) => {
    if (!moveMode) return;
    
    const newDate = info.date.toISOString();
    const task = tasks.find(t => t.id === moveMode.id);
    
    try {
      await tasksAPI.update(moveMode.id, { deadline: newDate });
      toast.success(`"${task?.title}" moved to ${info.date.toLocaleDateString()}`);
      
      setTasks(prev => prev.map(t => 
        t.id === moveMode.id ? { ...t, deadline: newDate } : t
      ));
      
      setMoveMode(null);
      setPopoverData(null);
    } catch (err) {
      toast.error('Failed to move task');
    }
  };

  // Start move mode from popover
  const startMoveMode = (event) => {
    setMoveMode(event);
    setPopoverData(null);
    toast.info('Click on a date to move the event there', { duration: 5000 });
  };

  // Cancel move mode
  const cancelMoveMode = () => {
    setMoveMode(null);
    toast.dismiss();
  };

  // Navigate calendar
  const handlePrev = () => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.prev();
      setCurrentDate(calendarApi.getDate());
    }
  };

  const handleNext = () => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.next();
      setCurrentDate(calendarApi.getDate());
    }
  };

  const handleToday = () => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.today();
      setCurrentDate(calendarApi.getDate());
    }
  };

  const formatMonthYear = (date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="section-title flex items-center gap-3">
              <CalendarIcon className="w-8 h-8 text-amber-500" />
              {t('calendar.techCalendar').toUpperCase()}
            </h1>
            <p className="text-slate-400 mt-1">
              {moveMode 
                ? t('calendar.moveMode')
                : t('calendar.dragEvents')}
            </p>
          </div>
          
          {/* Move Mode Indicator */}
          {moveMode && (
            <div className="flex items-center gap-2 bg-amber-500/20 border border-amber-500 rounded-lg px-4 py-2">
              <Move className="w-5 h-5 text-amber-500" />
              <span className="text-amber-400 font-medium">{t('calendar.moveMode')}: {moveMode.title}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={cancelMoveMode}
                className="text-amber-400 hover:text-white p-1 h-auto ml-2"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>

        {/* Priority Legend */}
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <span className="text-slate-400">{t('common.priority')}:</span>
          {Object.entries(PRIORITY_COLORS).map(([key, colors]) => (
            <div key={key} className="flex items-center gap-2">
              <div 
                className="w-4 h-4 rounded-sm" 
                style={{ backgroundColor: colors.bg }}
              />
              <span className="text-slate-300 capitalize">{t(`calendar.${key}Priority`)}</span>
            </div>
          ))}
        </div>

        {/* Custom Navigation */}
        <div className="card-industrial p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrev}
                className="btn-secondary"
                data-testid="prev-month-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleToday}
                className="btn-secondary"
              >
                Today
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNext}
                className="btn-secondary"
                data-testid="next-month-btn"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            <h2 className="text-xl font-bold text-white">
              {formatMonthYear(currentDate)}
            </h2>
          </div>

          {/* FullCalendar */}
          <div 
            className={`fullcalendar-container ${moveMode ? 'move-mode-active' : ''}`} 
            data-testid="calendar-container"
          >
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              events={events}
              editable={!moveMode}
              droppable={true}
              eventClick={handleEventClick}
              eventDrop={handleEventDrop}
              dateClick={handleDateClick}
              moreLinkClick={handleMoreLinkClick}
              dayMaxEvents={3}
              headerToolbar={false}
              height="auto"
              eventDisplay="block"
              displayEventTime={false}
              selectable={!!moveMode}
              eventContent={renderEventContent}
            />
          </div>
        </div>

        {/* Custom Popover */}
        {popoverData && (
          <div 
            className="fixed inset-0 z-50"
            onClick={() => setPopoverData(null)}
          >
            <div 
              className="absolute bg-slate-800 border border-slate-600 rounded-lg shadow-xl p-4 min-w-72"
              style={{
                left: Math.min(popoverData.position.x, window.innerWidth - 300),
                top: Math.min(popoverData.position.y, window.innerHeight - 350)
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-white font-semibold">
                  {popoverData.date.toLocaleDateString('en-US', { 
                    weekday: 'short', 
                    month: 'short', 
                    day: 'numeric' 
                  })}
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPopoverData(null)}
                  className="text-slate-400 hover:text-white p-1 h-auto"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <p className="text-slate-400 text-xs mb-3">
                Click <Move className="w-3 h-3 inline" /> to move, or click title to view
              </p>
              
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {popoverData.events.map((event) => (
                  <div
                    key={event.id}
                    className="flex items-center gap-2 p-2 rounded transition-opacity"
                    style={{
                      backgroundColor: event.backgroundColor,
                      color: event.textColor
                    }}
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => startMoveMode(event)}
                      className="p-1 h-auto opacity-70 hover:opacity-100"
                      title="Move to another date"
                    >
                      <Move className="w-4 h-4" />
                    </Button>
                    <span 
                      className="flex-1 truncate text-sm font-medium cursor-pointer hover:underline"
                      onClick={() => navigate(`/tasks/${event.id}`)}
                    >
                      <TranslatableSpan text={event.title} />
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Unscheduled Tasks Link */}
        {tasks.filter(t => !t.deadline && t.status !== 'completed').length > 0 && (
          <div className="card-industrial p-4">
            <p className="text-slate-400">
              <span className="text-amber-500 font-bold">
                +{tasks.filter(t => !t.deadline && t.status !== 'completed').length}
              </span> unscheduled work orders
              <a 
                href="/tasks" 
                className="text-amber-500 hover:text-amber-400 ml-2"
              >
                View all →
              </a>
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default CalendarPage;
